import { applyOp, type KeystrokeLog, type LogOp, type ScheduleOptions } from '@coderaw/log'

export type TintKind = 'stamp' | 'assisted'
export type TintRange = { readonly from: number; readonly to: number; readonly kind: TintKind }
export type DocState = {
  readonly n: number
  readonly doc: string
  readonly tints: readonly TintRange[]
}

export const EMPTY_DOC: DocState = { n: 0, doc: '', tints: [] }

/** Moves a tinted range through an edit: shifted, trimmed, or dropped if replaced. */
export const mapRange = (range: TintRange, op: LogOp): TintRange | null => {
  const delta = op.text.length - (op.to - op.from)
  if (range.to <= op.from) return range
  if (range.from >= op.to) return { ...range, from: range.from + delta, to: range.to + delta }
  const keepsLeft = range.from < op.from
  const keepsRight = range.to > op.to
  if (keepsLeft && keepsRight) return { ...range, to: range.to + delta }
  if (keepsLeft) return { ...range, to: op.from }
  if (keepsRight) return { ...range, from: op.from + op.text.length, to: range.to + delta }
  return null
}

const isTint = (kind: LogOp['kind']): kind is TintKind => kind === 'stamp' || kind === 'assisted'

export const stepDoc = (state: DocState, op: LogOp): DocState => {
  const mapped = state.tints.map((r) => mapRange(r, op)).filter((r): r is TintRange => r !== null)
  const added: TintRange[] =
    isTint(op.kind) && op.text.length > 0
      ? [{ from: op.from, to: op.from + op.text.length, kind: op.kind }]
      : []
  return { n: state.n + 1, doc: applyOp(state.doc, op), tints: [...mapped, ...added] }
}

export type DocCursor = { readonly at: (n: number) => DocState }

/** Walks forward incrementally; only rebuilds from scratch when asked to go backwards. */
export const createDocCursor = (log: KeystrokeLog): DocCursor => {
  let current = EMPTY_DOC
  return {
    at: (n) => {
      const target = Math.max(0, Math.min(n, log.ops.length))
      if (target < current.n) current = EMPTY_DOC
      while (current.n < target) current = stepDoc(current, log.ops[current.n] as LogOp)
      return current
    },
  }
}

/** The largest checkpoint that is at most n, or 0 when nothing has parsed yet. */
export const latestCheckpoint = (cps: readonly number[], n: number): number => {
  let lo = 0
  let hi = cps.length
  while (lo < hi) {
    const mid = (lo + hi) >>> 1
    if ((cps[mid] ?? Infinity) <= n) lo = mid + 1
    else hi = mid
  }
  return cps[lo - 1] ?? 0
}

export type ReplayEvents = { readonly deleted: boolean; readonly pause: number | undefined }

/** What happened between two op counts, for the flicker and the thinking label. */
export const eventsBetween = (log: KeystrokeLog, prevN: number, n: number): ReplayEvents => {
  const ops = log.ops.slice(Math.max(0, prevN), Math.max(prevN, n))
  const deleted = ops.some((op) => op.to > op.from)
  const pause = ops.reduce<number | undefined>((acc, op) => op.pause ?? acc, undefined)
  return { deleted, pause }
}

export const formatClock = (ms: number): string => {
  const total = Math.max(0, Math.floor(ms / 1000))
  const minutes = Math.floor(total / 60)
  const seconds = total % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

export const formatReal = (ms: number): string => {
  const total = Math.round(ms / 1000)
  if (total < 60) return `${total}s`
  const minutes = Math.floor(total / 60)
  if (minutes < 60) return `${minutes}m ${total % 60}s`
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`
}

export type SpeedChoice = 'auto' | '1' | '2' | '4'
export const SPEED_CHOICES: readonly SpeedChoice[] = ['auto', '1', '2', '4']

export const speedOptions = (choice: SpeedChoice): ScheduleOptions =>
  choice === 'auto' ? {} : { speed: Number(choice) }

export const speedLabel = (choice: SpeedChoice): string =>
  choice === 'auto' ? 'compressed' : `${choice}×`

export const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value))
